import { FC, useCallback, useEffect, useRef, useState } from 'react';

//* 표 칸을 좁게 유지하기 위한 클릭 툴팁.
//* "PG 취소 · 확정까지 2~3영업일" 같은 보조 문구를 셀에 그대로 두면 그 한 줄 때문에
//* 열 전체가 늘어난다. 자주 보는 값(상태 배지·일자)만 남기고 나머지는 여기로 접는다.
//*
//* hover(title)가 아니라 **클릭**인 이유: 표를 훑는 동안 마우스가 지나가기만 해도
//* 툴팁이 떠 시야를 가리는 걸 막고, 터치 환경에서도 열 수 있어야 하기 때문.
//*
//* 위치는 fixed + getBoundingClientRect — 표가 `overflow-x-auto` 안에 있어서
//* absolute로 띄우면 스크롤 컨테이너 경계에서 잘린다.

export interface CellDetailLine {
  /** 앞에 붙는 짧은 라벨 (없으면 본문만) */
  label?: string;
  text: string;
  tone?: 'default' | 'muted' | 'danger' | 'good' | 'warn';
}

const TONE: Record<NonNullable<CellDetailLine['tone']>, string> = {
  default: 'text-gray-700',
  muted: 'text-gray-400',
  danger: 'text-red-600',
  good: 'text-emerald-600',
  warn: 'text-amber-600',
};

export interface CellDetailPopoverProps {
  /** 빈 배열이면 버튼 자체를 그리지 않는다 (죽은 아이콘을 만들지 않기 위해) */
  lines: (CellDetailLine | null | undefined | false)[];
  title?: string;
  /** 버튼 hover 설명 */
  hint?: string;
}

export const CellDetailPopover: FC<CellDetailPopoverProps> = function CellDetailPopover({
  lines,
  title,
  hint = '자세히 보기',
}) {
  const rows = lines.filter(Boolean) as CellDetailLine[];
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setPos(null), []);

  useEffect(() => {
    if (!pos) return undefined;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target) || buttonRef.current?.contains(target)) {
        return;
      }
      close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    //* 표가 스크롤되면 좌표가 어긋나므로 그냥 닫는다 (따라다니게 만들 값어치가 없다)
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [pos, close]);

  if (rows.length === 0) return null;

  const toggle = () => {
    if (pos) return close();
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = 224; // w-56
    return setPos({
      top: rect.bottom + 4,
      //* 오른쪽 끝 열에서 화면 밖으로 나가지 않게 (좌측 8px 여백 보장)
      left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
    });
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        title={hint}
        aria-label={hint}
        className={`ml-1 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border text-[9px] font-bold leading-none ${
          pos
            ? 'border-gray-500 bg-gray-700 text-white'
            : 'border-gray-300 text-gray-400 hover:bg-gray-100 hover:text-gray-600'
        }`}
      >
        i
      </button>
      {pos && (
        <div
          ref={panelRef}
          style={{ top: pos.top, left: pos.left }}
          className="fixed z-50 w-56 whitespace-normal break-keep rounded-lg border bg-white p-2 text-left shadow-lg"
        >
          {title && (
            <div className="mb-1 border-b pb-1 text-[11px] font-semibold text-gray-700">
              {title}
            </div>
          )}
          <ul className="space-y-1">
            {rows.map((row, i) => (
              <li key={`${row.label ?? ''}-${row.text}-${i}`} className="text-[11px]">
                {row.label && <span className="mr-1 text-gray-400">{row.label}</span>}
                <span className={TONE[row.tone ?? 'default']}>{row.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
};
